import { type SyntheticEvent, useState } from 'react';
import { useSearchParams } from 'react-router';

import Tab from '@/componentsv2/Tab';
import Tabs from '@/componentsv2/Tabs';

import {
  canProposeAmendmentFor,
  deriveViewerRoleFromSubject,
  roleSubjectFromLive,
} from '../components/EngagementHub/roles';
import type { SessionViewer } from '../components/EngagementHub/roles';
import ActionStrip from './ActionStrip';
import AmendmentsPanel from './AmendmentsPanel';
import HoursPanel from './HoursPanel';
import HubHeader from './HubHeader';
import MessagesPanel from './MessagesPanel';
import OverviewPanel from './OverviewPanel';
import SettingsPanel from './SettingsPanel';
import BillingPanel from './billing/BillingPanel';
import type { EngagementView } from './viewModel';

export interface EngagementHubLiveProps {
  view: EngagementView;
  viewer: SessionViewer;
  /** The viewer's own profile id, for the Make Offer rate prefill. */
  viewerProfileId?: string | null;
  /** "Now" reference for relative timestamps; tests pin it. */
  now?: Date;
}

/**
 * The live Engagement Hub.
 *
 * Renders from `EngagementView` (the wire shape) rather than the prototype's
 * `EngagementScene`, which cannot be built from live data — see the module
 * comment in `viewModel.ts`. The prototype Hub stays as the Storybook / FE3–FE6
 * design surface; shared derivations (`roles.ts`, `status.ts`, `format.ts`,
 * `shared/connect.ts`) are imported from it, never re-implemented.
 */
export default function EngagementHubLive({
  view,
  viewer,
  viewerProfileId = null,
  now,
}: EngagementHubLiveProps): JSX.Element {
  const asOf = now ?? new Date();
  // The api answers "is this viewer OF staff" with the SAME predicate its
  // messaging wall uses, so prefer it over the session's superadmin flag, which
  // is a strict subset and would leave most OF staff looking like strangers to
  // their own UI (no composer, no explanation). The session value stays the
  // fallback for anything reached without an engagement in hand.
  const viewerRole = deriveViewerRoleFromSubject(
    { ...viewer, isOfStaff: view.viewerIsOfStaff || viewer.isOfStaff },
    roleSubjectFromLive(view)
  );
  const [searchParams, setSearchParams] = useSearchParams();
  // `?tab=` is an initial value, not a controlled one: the dashboard deep-links
  // an expert straight to the tab their next action lives on, and after that the
  // tabs are the user's. Resolved against the built list below, so a tab that
  // isn't rendered for this viewer falls through to Overview rather than 404ing.
  const [requestedTab] = useState(() => searchParams.get('tab'));
  // Keyed by tab KEY, not index: the Amendments tab comes and goes with
  // eligibility (an engagementHub refetch can flip it), and a numeric value
  // would silently land the viewer on whichever tab shifted into their slot.
  const [activeTab, setActiveTab] = useState<string | null>(null);

  /**
   * The Amendments tab is wider than FE1's accepted-only gate: it hosts the live
   * proposal, the settled history and the propose entry point alike. Any
   * amendment at all earns it — including a countered or withdrawn one, which
   * the server's `hasAcceptedAmendments` predicate does not count and which
   * would otherwise leave an engagement that was amended with no tab to say so.
   * So does an empty history, whenever this viewer could start the first one.
   */
  const showAmendments =
    view.amendments.length > 0 || canProposeAmendmentFor(view.status, viewerRole);

  /**
   * ENG-5428's server predicate, verbatim (FE4 / ENG-5450): true once a
   * confirming hours submission or a non-skipped billing period exists, and
   * monotone — hidden before then, never an empty-but-visible tab.
   */
  const showBilling = view.hasBillingActivity;

  const tabs = [
    {
      key: 'overview',
      label: 'Overview',
      node: (
        <OverviewPanel
          view={view}
          viewerRole={viewerRole}
          viewerUserId={viewer.userId}
          now={asOf}
        />
      ),
    },
    {
      key: 'hours',
      label: 'Hours',
      node: <HoursPanel view={view} viewerRole={viewerRole} viewerUserId={viewer.userId} />,
    },
    {
      key: 'messages',
      label: 'Messages',
      node: (
        <MessagesPanel
          view={view}
          viewerRole={viewerRole}
          viewerUserId={viewer.userId}
          now={asOf}
        />
      ),
    },
    ...(showAmendments
      ? [
          {
            key: 'amendments',
            label: 'Amendments',
            node: (
              <AmendmentsPanel view={view} viewerRole={viewerRole} viewerUserId={viewer.userId} />
            ),
          },
        ]
      : []),
    ...(showBilling
      ? [
          {
            key: 'billing',
            label: 'Billing',
            node: <BillingPanel view={view} viewer={viewer} />,
          },
        ]
      : []),
    { key: 'settings', label: 'Settings', node: <SettingsPanel view={view} /> },
  ];

  const current = tabs.find((t) => t.key === (activeTab ?? requestedTab)) ?? tabs[0];

  /**
   * The deep link is spent the moment the user steers away from it. Leaving
   * `?tab=` behind would let any remount drag them back to it, and would leave
   * the URL they copy or bookmark describing a tab they deliberately left.
   * Dropped only on that first divergence, so the arriving link stays
   * reshareable for as long as it still describes where the viewer is.
   */
  function selectTab(key: string) {
    setActiveTab(key);
    if (!searchParams.has('tab')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="mx-auto flex max-w-[1200px] flex-col gap-24 bg-surface-base p-24 md:p-32">
      <HubHeader view={view} viewerRole={viewerRole} viewerUserId={viewer.userId} />

      <ActionStrip
        view={view}
        viewerRole={viewerRole}
        viewerUserId={viewer.userId}
        viewerProfileId={viewerProfileId}
      />

      <div>
        <Tabs
          value={current.key}
          onChange={(_e: SyntheticEvent, v: string) => selectTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabs.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
      </div>

      <div>{current.node}</div>
    </div>
  );
}
